/** Petits utilitaires transverses, sans dépendance externe. */

/** Concatène des classes CSS conditionnelles. */
export function classes(...valeurs: Array<string | false | null | undefined>): string {
  return valeurs.filter((v): v is string => typeof v === 'string' && v !== '').join(' ');
}

/**
 * Identifiant unique, utilisé comme clé d'idempotence (`Idempotency-Key`) et comme clé de liste.
 * `crypto.randomUUID` est disponible sur tout navigateur servi en HTTPS ou sur 127.0.0.1 ;
 * le repli couvre le cas d'un back-office servi en clair sur une IP de réseau local.
 */
export function identifiantUnique(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();

  const octets = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') {
    c.getRandomValues(octets);
  } else {
    for (let i = 0; i < octets.length; i += 1) octets[i] = Math.floor(Math.random() * 256);
  }
  // Version 4, variante RFC 4122.
  octets[6] = ((octets[6] ?? 0) & 0x0f) | 0x40;
  octets[8] = ((octets[8] ?? 0) & 0x3f) | 0x80;

  const hex = Array.from(octets, (o) => o.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** Attente asynchrone (simulation de latence du mode maquette). */
export function patienter(ms: number): Promise<void> {
  return new Promise((resoudre) => {
    setTimeout(resoudre, ms);
  });
}

/** Borne une valeur dans un intervalle. */
export function borner(valeur: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, valeur));
}

/** Somme d'un tableau de nombres. */
export function somme(valeurs: readonly number[]): number {
  return valeurs.reduce((total, v) => total + v, 0);
}

/** Regroupe des éléments par clé calculée. */
export function grouperPar<T, C extends string>(
  elements: readonly T[],
  cle: (element: T) => C,
): Map<C, T[]> {
  const groupes = new Map<C, T[]>();
  for (const element of elements) {
    const k = cle(element);
    const existant = groupes.get(k);
    if (existant) existant.push(element);
    else groupes.set(k, [element]);
  }
  return groupes;
}

/**
 * Déclenche le téléchargement d'un contenu texte généré côté navigateur.
 * Utilisé pour les exports CSV : aucune donnée ne repasse par le serveur.
 */
export function telechargerTexte(nomFichier: string, contenu: string, type = 'text/csv'): void {
  // Le BOM UTF-8 évite qu'Excel massacre les accents à l'ouverture d'un CSV.
  const bom = type.startsWith('text/csv') ? '﻿' : '';
  const blob = new Blob([bom + contenu], { type: `${type};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const lien = document.createElement('a');
  lien.href = url;
  lien.download = nomFichier;
  document.body.appendChild(lien);
  lien.click();
  lien.remove();
  URL.revokeObjectURL(url);
}

/** Copie une valeur dans le presse-papiers. Renvoie faux si le navigateur l'interdit. */
export async function copierPressePapiers(texte: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(texte);
    return true;
  } catch {
    return false;
  }
}

/** Horodatage compact `20260730-1403`, pour nommer les fichiers exportés. */
export function horodatageFichier(date: Date = new Date()): string {
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${date.getFullYear()}${p(date.getMonth() + 1)}${p(date.getDate())}-${p(date.getHours())}${p(date.getMinutes())}`;
}
